"use client";

import React, { useState } from "react";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_TYPE_OPTIONS = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"];
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"];
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"];
export default function EstimateRequest() {
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [projectType, setProjectType] = useState("");
  const [timing, setTiming] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setZipError("");
    setConsentError("");
    setErrorMessage("");
    let isValid = true;
    if (!/^\d{5}$/.test(zip.trim())) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      isValid = false;
    }
    if (!consent) {
      setConsentError("Please agree to the contact terms to proceed.");
      isValid = false;
    }
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      const payload = new URLSearchParams({
        name: fullName,
        phone,
        email,
        project_type: projectType,
        address: zip,
        zip,
        details: message,
        timing,
        budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: typeof window !== "undefined" ? window.location.href : "",
        form_location: "remodeling-planning-guide-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: payload
      });
      if (response.ok) {
        setSubmissionSuccess(true);
      } else {
        setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
      }
    } catch {
      setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative bg-background text-foreground py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-serif font-bold tracking-tight text-foreground">
            Request an Estimate
          </h2>
          <p className="mt-3 text-base text-foreground/80 max-w-xl mx-auto">
            Tell us about your home and renovation vision. We review every project plan and follow up with a tailored preliminary consultation.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card/85 backdrop-blur shadow-xl p-6 sm:p-10 transition-all">
          {submissionSuccess ? <div className="py-12 text-center flex flex-col items-center">
              <div className="size-14 rounded-full bg-primary/20 flex items-center justify-center mb-4 text-primary-foreground">
                <CheckCircle2 className="size-8 text-primary" />
              </div>
              <h3 className="text-2xl font-serif font-semibold text-foreground">
                Estimate Request Received
              </h3>
              <p className="mt-2 text-foreground/80 max-w-md">
                Thank you for contacting Brookhaven Elevation Renovations. Our team will review your project details and reach out shortly.
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-6">
              {errorMessage && <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Unable to Send Request</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input id="fullName" type="text" required value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Jane Doe" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input id="phone" type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="(404) 555-0123" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zip">ZIP Code *</Label>
                  <Input id="zip" type="text" required value={zip} onChange={e => {
                setZip(e.target.value);
                if (zipError) setZipError("");
              }} placeholder="30319" aria-invalid={!!zipError} />
                  {zipError && <p className="text-xs text-destructive mt-1">{zipError}</p>}
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="projectType">Project Type *</Label>
                  <select id="projectType" required value={projectType} onChange={e => setProjectType(e.target.value)} className="border-input text-foreground dark:bg-input/30 flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:text-sm">
                    <option value="" disabled className="bg-card text-foreground">
                      Select project type
                    </option>
                    {PROJECT_TYPE_OPTIONS.map(opt => <option key={opt} value={opt} className="bg-card text-foreground">
                        {opt}
                      </option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timing">Preferred Timing</Label>
                  <select id="timing" value={timing} onChange={e => setTiming(e.target.value)} className="border-input text-foreground dark:bg-input/30 flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:text-sm">
                    <option value="" className="bg-card text-foreground">
                      Select timeline (optional)
                    </option>
                    {TIMING_OPTIONS.map(opt => <option key={opt} value={opt} className="bg-card text-foreground">
                        {opt}
                      </option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="budget">Budget Range</Label>
                  <select id="budget" value={budget} onChange={e => setBudget(e.target.value)} className="border-input text-foreground dark:bg-input/30 flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:text-sm">
                    <option value="" className="bg-card text-foreground">
                      Select range (optional)
                    </option>
                    {BUDGET_OPTIONS.map(opt => <option key={opt} value={opt} className="bg-card text-foreground">
                        {opt}
                      </option>)}
                  </select>
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="message">Project Details / Message</Label>
                  <Textarea id="message" value={message} onChange={e => setMessage(e.target.value)} rows={4} placeholder="Tell us about the scope, architectural ideas, or any questions you have..." />
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox id="consent" checked={consent} onCheckedChange={checked => {
                setConsent(checked === true);
                if (consentError) setConsentError("");
              }} className="mt-0.5" />
                  <Label htmlFor="consent" className="text-xs text-foreground/85 leading-normal cursor-pointer">
                    I agree to be contacted about my project by phone, text, or email.
                  </Label>
                </div>
                {consentError && <p className="text-xs text-destructive">{consentError}</p>}
              </div>

              <div className="pt-4">
                <Button type="submit" disabled={isSubmitting} size="lg" className="w-full sm:w-auto px-8 bg-primary text-primary-foreground font-semibold shadow-md hover:bg-primary/90 transition-colors">
                  {isSubmitting ? <>
                      <Loader2 className="size-4 animate-spin mr-2" />
                      Submitting Request...
                    </> : "Request an Estimate"}
                </Button>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}